<template>
  <q-dialog v-model="isOpen" :maximized="$q.screen.width < 700">
    <q-card style="min-width: 50vw;">
      <q-card-section class="row">
        <div class="text-h6 text-center">
          {{ $t('Allocate by document') }}
        </div>

        <q-space/>

        <q-btn
            color="transparent"
            text-color="dark"
            size="sm"
            icon="close"
            no-caps
            unelevated
            @click="hide"
        />
      </q-card-section>

      <q-card-section v-if="isLoading" class="q-pa-md text-center">
        <q-spinner color="light-blue-9" size="3rem"/>
      </q-card-section>

      <q-card-section v-else>
        <form-builder class="q-pa-sm" :schema="schema"/>
      </q-card-section>

      <q-card-section class="text-center q-pt-none">
        <q-btn
            color="dark"
            text-color="white"
            class="q-mr-sm"
            :label="$t('Close')"
            @click="hide"
        />
        <q-btn
            color="light-blue-9"
            text-color="white"
            :label="$t('Save')"
            :disable="!model.place"
            @click="save"
        />
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { mapMutations } from 'vuex'

export default {
  name: 'AllocateDocumentModal',
  props: {
    show: { type: Boolean, default: false },
    acceptance: { type: Object, default: null }
  },
  emits: ['submit', 'close'],
  data () {
    return {
      isOpen: false,
      isLoading: false,
      acceptanceData: null,
      warehouse: null,
      model: {
        place: null
      },
      selectedType: { id: 'Orderadmin\\Storage\\Entity\\Place', label: 'Static' }
    }
  },
  watch: {
    show: {
      immediate: true,
      handler (visible) {
        if (visible && this.acceptance) {
          this.openWithAcceptance(this.acceptance)
        } else {
          this.isOpen = false
        }
      }
    },
    isOpen (val) {
      if (!val) this.$emit('close')
    }
  },
  computed: {
    schema () {
      return {
        fields: [
          {
            type: 'multiselect',
            label: this.$t('Select a storage location'),
            field: 'place',
            value: this.model.place,
            customLabel: (row) => {
              if (row && typeof row === 'object') {
                const path = this.loadPath(row)
                return `${path.join(' - ')} (${row.code}*) (${row.type})`
              }
              return row
            },
            onScroll: (search, page) => {
              if (!this.warehouse) return Promise.resolve({ items: [], totalItems: 0 })
              const query = {
                per_page: 25,
                page,
                search,
                filter: [
                  { type: 'in', field: 'state', values: ['active', 'normal'] },
                  { type: 'instanceof', field: 'p', class: this.selectedType.id }
                ]
              }
              const value = this.warehouse.type === 'virtual'
                ? this.getId(this.warehouse._embedded.parent)
                : this.warehouse.id
              query.filter.push({ type: 'eq', field: 'warehouse', value })
              return this.$service.storagePlace.getAll(query)
            },
            onChange: (place) => {
              this.model.place = place
            }
          }
        ]
      }
    }
  },
  methods: {
    ...mapMutations(['addErrorNotification']),
    getId (warehouse) {
      if (warehouse.id) return warehouse.id
      return warehouse._links.self.href.split('/').pop()
    },
    loadPath (place) {
      if (!place.path) {
        if (place.type === 'room') return [(place.placeAlias || '')]
        return [place.room, place.row, place.rack, place.shelf].map(x => x || 0)
      }
      return [...place.path, (place.placeAlias || '')]
    },
    getDocumentAllocationFilter () {
      const acceptance = this.acceptanceData || this.acceptance
      if (!acceptance) return []
      return [
        { type: 'in', field: 'createdByDocument', values: [acceptance.id] },
        {
          type: 'orx',
          conditions: [
            { field: 'state', type: 'eq', value: 'new' },
            {
              type: 'andx',
              conditions: [
                { field: 'state', type: 'eq', value: 'blocked' },
                { field: 'place', type: 'isNull' },
                { field: 'reserve', type: 'isNull' }
              ],
              where: 'and'
            },
            {
              type: 'andx',
              conditions: [
                { field: 'state', type: 'eq', value: 'normal' },
                { field: 'place', type: 'isNull' }
              ],
              where: 'and'
            }
          ],
          where: 'and'
        }
      ]
    },
    openWithAcceptance (acceptance) {
      this.acceptanceData = acceptance
      this.model.place = null
      return Promise.resolve(acceptance._embedded?.warehouse)
        .then(warehouse => {
          if (!warehouse) return Promise.reject(new Error('No warehouse'))
          return warehouse.type === 'virtual' && warehouse._embedded
            ? warehouse
            : this.$service.warehouse.get(warehouse.id)
        })
        .then(warehouse => {
          this.warehouse = warehouse
          this.isOpen = true
        })
        .catch(err => {
          this.addErrorNotification(err)
        })
    },
    hide () {
      this.isOpen = false
    },
    save () {
      if (!this.model.place || !this.model.place.id) {
        this.addErrorNotification(new Error('Select a storage location'))
        return
      }
      const acceptance = this.acceptanceData || this.acceptance
      const received = Number(acceptance?.unitsReceived) || 0
      const allocated = Number(acceptance?.unitsAllocated) || 0
      const perPage = Math.max(received - allocated, 1)
      this.isLoading = true
      const placeId = this.model.place.id
      const filter = this.getDocumentAllocationFilter()
      const query = { per_page: perPage, filter }
      const data = [{ place: placeId, state: 'normal' }]

      this.$service.storageItemEntity.patch(data, query, 'application/json+offers', placeId)
        .then(() => {
          this.isLoading = false
          this.hide()
          this.$emit('submit')
        })
        .catch(err => {
          this.isLoading = false
          this.addErrorNotification(err)
        })
    }
  }
}
</script>
